const headerNavigationEditorState = new Map();

class HeaderNavigation extends HTMLElement {
  constructor() {
    super();
    this.desktopMedia = window.matchMedia("(min-width: 1140px)");
    this.reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.panelStates = new Map();
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.surface = this.querySelector("[data-nav-surface]");
    this.nav = this.querySelector("[data-nav-root]");
    this.list = this.querySelector("[data-nav-list]");
    this.drawerToggle = this.querySelector("[data-nav-drawer-toggle]");
    this.closeButton = this.querySelector("[data-nav-close]");
    this.headerWrapper = this.closest(".header-wrapper");
    this.sectionHeader = this.closest(".section-header");
    this.pointerHasMoved = false;
    this.boundItems = new WeakSet();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener("click", this.onClick.bind(this), { signal });
    this.addEventListener("keydown", this.onKeyDown.bind(this), { signal });
    this.refreshItems();
    this.desktopMedia.addEventListener("change", this.onBreakpointChange.bind(this), { signal });
    window.addEventListener("resize", this.onResize.bind(this), { signal });
    document.addEventListener("pointerdown", this.onDocumentPointerDown.bind(this), { signal });
    document.addEventListener("pointermove", this.onDocumentPointerMove.bind(this), { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:block:select", this.onBlockSelect.bind(this), { signal });
      document.addEventListener("shopify:block:deselect", this.onBlockDeselect.bind(this), { signal });
    }

    this.readyFrame = requestAnimationFrame(() => {
      if (!this.initialized) return;
      this.classList.add("is-ready");
      this.restoreEditorSelection();
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.readyFrame);
    cancelAnimationFrame(this.editorDeselectFrame);
    this.closeAll(false, true, true);
    this.abortController?.abort();
    this.classList.remove("is-ready");
    this.initialized = false;
  }

  bindItem(item, signal) {
    item.addEventListener(
      "pointerenter",
      (event) => {
        if (
          event.pointerType === "mouse" &&
          this.pointerHasMoved &&
          this.classList.contains("is-ready") &&
          this.isDesktop &&
          this.desktopBehavior === "link_hover_panel"
        ) {
          this.openItem(item);
        }
      },
      { signal }
    );
    item.addEventListener(
      "pointerleave",
      () => {
        if (this.isDesktop && this.desktopBehavior === "link_hover_panel") this.closeItem(item);
      },
      { signal }
    );
    item.addEventListener(
      "focusin",
      () => {
        if (this.isDesktop && this.desktopBehavior === "link_hover_panel") this.openItem(item);
      },
      { signal }
    );
    item.addEventListener(
      "focusout",
      () => {
        if (!this.isDesktop || this.desktopBehavior !== "link_hover_panel") return;
        setTimeout(() => {
          if (!item.contains(document.activeElement)) this.closeItem(item);
        });
      },
      { signal }
    );
  }

  refreshItems() {
    const items = Array.from(this.querySelectorAll("[data-nav-item]"));

    this.panelStates.forEach((state, item) => {
      if (items.includes(item)) return;
      this.cancelPanelWork(item);
      this.panelStates.delete(item);
    });

    if (this.openItemElement && !items.includes(this.openItemElement)) {
      this.openItemElement = null;
    }

    this.items = items;
    this.items.forEach((item) => {
      if (this.boundItems.has(item)) return;
      this.boundItems.add(item);
      this.bindItem(item, this.abortController.signal);
    });
  }

  get isDesktop() {
    return this.desktopMedia.matches;
  }

  get desktopBehavior() {
    return this.dataset.desktopBehavior || "link_hover_panel";
  }

  get reducedMotion() {
    return this.reducedMotionMedia.matches;
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  getBlockKey(item) {
    return item?.dataset.blockId?.split("__").at(-1);
  }

  resolveEditorItem(target) {
    if (!(target instanceof Element) || !this.contains(target)) return null;

    return target.closest("[data-nav-item]") || target.querySelector("[data-nav-item]");
  }

  restoreEditorSelection() {
    if (!window.Shopify?.designMode || !this.sectionId) return;

    this.refreshItems();
    const blockKey = headerNavigationEditorState.get(this.sectionId);
    if (!blockKey) return;

    const item = this.items.find((candidate) => this.getBlockKey(candidate) === blockKey);
    if (!item?.querySelector("[data-nav-panel]")) return;

    item.dataset.editorOpen = "true";
    if (this.isDesktop) {
      this.openItem(item);
    } else {
      this.openDrawer();
      this.openMobileSubmenu(item);
    }
  }

  clearEditorSelection(item = this.items.find((candidate) => candidate.dataset.editorOpen === "true")) {
    if (this.sectionId) headerNavigationEditorState.delete(this.sectionId);
    if (!item) return;

    delete item.dataset.editorOpen;
    if (this.isDesktop) {
      this.closeItem(item, false, true);
    } else {
      this.closeMobileSubmenu(item, false, true);
    }
  }

  pinEditorItem(item) {
    const blockKey = this.getBlockKey(item);
    if (this.sectionId && blockKey) {
      headerNavigationEditorState.set(this.sectionId, blockKey);
    }

    this.items.forEach((candidate) => {
      if (candidate === item) return;
      delete candidate.dataset.editorOpen;
      this.closeItem(candidate, true, true);
    });

    item.dataset.editorOpen = "true";
    if (this.isDesktop) {
      this.openItem(item);
    } else {
      this.openDrawer();
      this.openMobileSubmenu(item);
    }
  }

  onClick(event) {
    const drawerToggle = event.target.closest("[data-nav-drawer-toggle]");
    if (drawerToggle) {
      this.isDrawerOpen ? this.closeDrawer() : this.openDrawer();
      return;
    }

    if (event.target.closest("[data-nav-close], [data-nav-overlay]")) {
      this.closeDrawer();
      return;
    }

    const backButton = event.target.closest("[data-nav-back]");
    if (backButton) {
      const item = backButton.closest("[data-nav-item]");
      this.closeMobileSubmenu(item);
      return;
    }

    const toggle = event.target.closest("[data-nav-toggle]");
    if (!toggle || !this.contains(toggle)) return;

    const item = toggle.closest("[data-nav-item]");
    if (!item) return;

    if (this.isDesktop) {
      this.toggleItem(item);
    } else {
      this.openMobileSubmenu(item);
    }
  }

  onKeyDown(event) {
    if (event.key !== "Escape") return;

    if (!this.isDesktop && this.openItemElement) {
      event.preventDefault();
      this.closeMobileSubmenu(this.openItemElement);
      return;
    }

    if (!this.isDesktop && this.isDrawerOpen) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    if (this.isDesktop && this.openItemElement) {
      event.preventDefault();
      const item = this.openItemElement;
      this.closeItem(item);
      item.querySelector("[data-nav-toggle], [data-nav-link]")?.focus();
    }
  }

  onBreakpointChange() {
    this.closeAll(false, true);
  }

  onResize() {
    if (!this.isDrawerOpen) return;
    this.style.setProperty("--viewport-height", `${window.innerHeight}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${Math.floor(this.sectionHeader?.getBoundingClientRect().bottom || 0)}px`
    );
  }

  onDocumentPointerDown(event) {
    if (!this.isDesktop || !this.openItemElement) return;
    if (!this.openItemElement.contains(event.target)) this.closeItem(this.openItemElement);
  }

  onDocumentPointerMove(event) {
    if (event.pointerType === "mouse") this.pointerHasMoved = true;
  }

  openDrawer() {
    if (this.isDesktop || this.isDrawerOpen) return;

    this.isDrawerOpen = true;
    this.style.setProperty("--viewport-height", `${window.innerHeight}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${Math.floor(this.sectionHeader?.getBoundingClientRect().bottom || 0)}px`
    );
    this.classList.add("is-drawer-open");
    this.sectionHeader?.classList.add("menu-open");
    document.body.classList.add("header-navigation-open");
    this.drawerToggle?.setAttribute("aria-expanded", "true");
    this.headerWrapper && (this.headerWrapper.preventHide = true);

    requestAnimationFrame(() => {
      if (typeof trapFocus === "function" && this.nav) trapFocus(this.nav, this.closeButton || this.nav);
    });
  }

  closeDrawer(restoreFocus = true, immediate = false, force = false) {
    if (!this.isDrawerOpen && !this.classList.contains("is-drawer-open")) return;
    if (!force && this.openItemElement?.dataset.editorOpen === "true") return;

    this.closeItems(immediate, force);
    this.isDrawerOpen = false;
    this.classList.remove("is-drawer-open");
    this.nav?.classList.remove("is-submenu-open");
    this.sectionHeader?.classList.remove("menu-open");
    document.body.classList.remove("header-navigation-open");
    this.drawerToggle?.setAttribute("aria-expanded", "false");
    this.headerWrapper && (this.headerWrapper.preventHide = false);

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(restoreFocus ? this.drawerToggle : null);
    }
  }

  toggleItem(item) {
    item.classList.contains("is-open") ? this.closeItem(item) : this.openItem(item);
  }

  openItem(item) {
    const panel = item?.querySelector("[data-nav-panel]");
    if (!panel) return;

    const editorOpenItem = this.items.find((candidate) => candidate.dataset.editorOpen === "true");
    if (editorOpenItem && editorOpenItem !== item) return;

    this.items.forEach((candidate) => {
      if (candidate !== item) this.closeItem(candidate);
    });

    this.cancelPanelWork(item);
    panel.hidden = false;
    panel.inert = false;
    panel.setAttribute("aria-hidden", "false");
    this.setControlsExpanded(item, true);
    this.openItemElement = item;
    this.headerWrapper && (this.headerWrapper.preventHide = true);

    if (this.isDesktop) {
      document.documentElement.style.setProperty(
        "--header-bottom-position-desktop",
        `${Math.floor(this.headerWrapper?.getBoundingClientRect().bottom || 0)}px`
      );
    }

    if (!this.classList.contains("is-ready") || this.reducedMotion) {
      item.classList.add("is-open");
      return;
    }

    panel.getBoundingClientRect();
    const state = this.getPanelState(item);
    state.openFrame = requestAnimationFrame(() => {
      state.openFrame = null;
      if (!panel.hidden && panel.getAttribute("aria-hidden") === "false") {
        item.classList.add("is-open");
      }
    });
  }

  closeItem(item, immediate = false, force = false) {
    if (!item) return;
    if (!force && item.dataset.editorOpen === "true") return;

    const panel = item.querySelector("[data-nav-panel]");
    if (!panel) return;

    this.cancelPanelWork(item);
    item.classList.remove("is-open");
    this.setControlsExpanded(item, false);
    panel.setAttribute("aria-hidden", "true");
    panel.inert = true;

    if (immediate || panel.hidden || !this.classList.contains("is-ready") || this.reducedMotion) {
      this.finishPanelClose(item, panel);
    } else {
      const state = this.getPanelState(item);
      const finish = (event) => {
        if (event && event.target !== panel) return;
        this.finishPanelClose(item, panel);
      };
      state.onTransitionEnd = finish;
      panel.addEventListener("transitionend", finish);
      state.closeTimer = window.setTimeout(finish, this.getTransitionDuration(panel) + 50);
    }

    if (this.openItemElement === item) this.openItemElement = null;
    if (this.isDesktop && !this.openItemElement && this.headerWrapper) {
      this.headerWrapper.preventHide = false;
    }
  }

  setControlsExpanded(item, expanded) {
    item.querySelectorAll("[data-nav-toggle], [data-nav-disclosure]").forEach((control) => {
      control.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  }

  getPanelState(item) {
    if (!this.panelStates.has(item)) this.panelStates.set(item, {});
    return this.panelStates.get(item);
  }

  cancelPanelWork(item) {
    const state = this.getPanelState(item);
    const panel = item.querySelector("[data-nav-panel]");

    if (state.openFrame) cancelAnimationFrame(state.openFrame);
    if (state.closeTimer) clearTimeout(state.closeTimer);
    if (state.onTransitionEnd && panel) panel.removeEventListener("transitionend", state.onTransitionEnd);

    state.openFrame = null;
    state.closeTimer = null;
    state.onTransitionEnd = null;
  }

  finishPanelClose(item, panel) {
    this.cancelPanelWork(item);
    if (item.classList.contains("is-open")) return;
    panel.hidden = true;
  }

  getTransitionDuration(element) {
    const styles = window.getComputedStyle(element);
    const durations = styles.transitionDuration.split(",").map(this.timeToMilliseconds);
    const delays = styles.transitionDelay.split(",").map(this.timeToMilliseconds);
    const count = Math.max(durations.length, delays.length);
    let longest = 0;

    for (let index = 0; index < count; index += 1) {
      longest = Math.max(longest, durations[index % durations.length] + delays[index % delays.length]);
    }

    return longest;
  }

  timeToMilliseconds(value) {
    const time = value.trim();
    return time.endsWith("ms") ? Number.parseFloat(time) : Number.parseFloat(time) * 1000;
  }

  openMobileSubmenu(item) {
    if (!item?.querySelector("[data-nav-panel]")) return;
    this.openItem(item);
    this.nav?.classList.add("is-submenu-open");

    const panel = item.querySelector("[data-nav-panel]");
    const backButton = panel?.querySelector("[data-nav-back]");
    requestAnimationFrame(() => {
      if (typeof trapFocus === "function" && panel) trapFocus(panel, backButton || panel);
    });
  }

  closeMobileSubmenu(item, restoreFocus = true, force = false) {
    if (!item) return;
    const toggle = item.querySelector("[data-nav-toggle]");
    this.closeItem(item, false, force);
    if (!force && item.dataset.editorOpen === "true") return;
    this.nav?.classList.remove("is-submenu-open");

    if (typeof removeTrapFocus === "function") removeTrapFocus();
    if (this.isDrawerOpen && typeof trapFocus === "function" && this.nav) {
      trapFocus(this.nav, restoreFocus ? toggle || this.closeButton : this.closeButton || this.nav);
    }
  }

  closeItems(immediate = false, force = false) {
    this.items.forEach((item) => this.closeItem(item, immediate, force));
    if (force || this.openItemElement?.dataset.editorOpen !== "true") {
      this.openItemElement = null;
    }
  }

  closeAll(restoreFocus = false, immediate = false, force = false) {
    if (this.isDrawerOpen || this.classList.contains("is-drawer-open")) {
      this.closeDrawer(restoreFocus, immediate, force);
    } else {
      this.closeItems(immediate, force);
      this.nav?.classList.remove("is-submenu-open");
      this.headerWrapper && (this.headerWrapper.preventHide = false);
    }
  }

  onBlockSelect(event) {
    cancelAnimationFrame(this.editorDeselectFrame);
    this.editorDeselectFrame = null;
    this.refreshItems();

    const item = this.resolveEditorItem(event.target);
    if (!item?.querySelector("[data-nav-panel]")) {
      this.clearEditorSelection();
      return;
    }

    this.pinEditorItem(item);

    if (event.detail?.load) {
      requestAnimationFrame(() => this.restoreEditorSelection());
    }
  }

  onBlockDeselect(event) {
    const item = this.resolveEditorItem(event.target);
    if (!item || item.dataset.editorOpen !== "true") return;

    cancelAnimationFrame(this.editorDeselectFrame);
    const blockKey = this.getBlockKey(item);
    this.editorDeselectFrame = requestAnimationFrame(() => {
      this.editorDeselectFrame = null;
      if (headerNavigationEditorState.get(this.sectionId) !== blockKey) return;
      this.refreshItems();
      const currentItem = this.items.find((candidate) => this.getBlockKey(candidate) === blockKey);
      this.clearEditorSelection(currentItem || item);
    });
  }
}

if (!customElements.get("header-navigation")) {
  customElements.define("header-navigation", HeaderNavigation);
}

if (window.Shopify?.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const navigation = event.target.matches?.("header-navigation")
      ? event.target
      : event.target.querySelector("header-navigation");
    requestAnimationFrame(() => {
      navigation?.refreshItems();
      navigation?.restoreEditorSelection();
    });
  });
}
